import React, { useState } from 'react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { Badge } from '../../design-system/Badge';
import { Button } from '../../design-system/Button';
import {
  Clock,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Calendar,
  CalendarCheck2
} from 'lucide-react';

export const MyWorkPage = () => {
  const { allTasks = [], setSelectedTask, updateTask, projects = [] } = useWorkspace();
  const [activeTab, setActiveTab] = useState('today'); // 'today', 'upcoming', 'overdue', 'blocked', 'completed'

  // Filter tasks assigned to current user ('Rohith Prince' or all assigned in single user mode)
  const myTasks = allTasks.filter((t) => {
    if (!t.assignee) return true; // show unassigned for awareness or assigned
    const aName = typeof t.assignee === 'object' ? t.assignee?.name : t.assignee;
    return aName === 'Rohith Prince' || true;
  });

  const todayStr = '2026-08-22';

  // Categories
  const todayTasks = myTasks.filter(
    (t) => (t.dueDate === todayStr || t.status === 'in_progress') && t.status !== 'done'
  );

  const upcomingTasks = myTasks.filter(
    (t) => t.dueDate && t.dueDate > todayStr && t.status !== 'done'
  );

  const overdueTasks = myTasks.filter(
    (t) => t.dueDate && t.dueDate < todayStr && t.status !== 'done'
  );

  const blockedTasks = myTasks.filter((t) => {
    if (t.status === 'done') return false;
    const blockedByList = t.blockedBy || [];
    const activePrereqs = blockedByList
      .map((id) => allTasks.find((item) => item.id === id))
      .filter((item) => item && item.status !== 'done');
    return activePrereqs.length > 0;
  });

  const completedTasks = myTasks.filter((t) => t.status === 'done');

  const getProjectInfo = (projectId) => {
    const prj = projects.find((p) => p.id === projectId);
    return prj ? { name: prj.name, key: prj.key } : { name: 'Forma Platform', key: 'FOR' };
  };

  const getActiveList = () => {
    switch (activeTab) {
      case 'upcoming':
        return upcomingTasks;
      case 'overdue':
        return overdueTasks;
      case 'blocked':
        return blockedTasks;
      case 'completed':
        return completedTasks;
      case 'today':
      default:
        return todayTasks;
    }
  };

  const activeTaskList = getActiveList();

  const handleMarkDone = (e, taskId) => {
    e.stopPropagation();
    updateTask(taskId, { status: 'done' });
  };

  const handleStartTask = (e, taskId) => {
    e.stopPropagation();
    updateTask(taskId, { status: 'in_progress' });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }} className="animate-fade-in">
      {/* Page Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '800' }}>My Work</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
            Unified personal action center across all workspace projects
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
            Saturday, Aug 22, 2026
          </span>
        </div>
      </div>

      {/* Summary Metrics Bar */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px'
        }}
      >
        <div
          onClick={() => setActiveTab('today')}
          style={{
            padding: '14px 16px',
            backgroundColor: activeTab === 'today' ? 'rgba(35, 190, 179, 0.08)' : 'var(--surface-card)',
            boxShadow: 'var(--shadow-raised-sm)',
            borderRadius: 'var(--radius-lg)',
            border: activeTab === 'today' ? '1px solid var(--accent-teal)' : '1px solid var(--border-subtle)',
            cursor: 'pointer',
            transition: 'all 0.15s'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)' }}>TODAY</span>
            <Clock size={16} color="var(--accent-teal)" />
          </div>
          <div style={{ fontSize: '22px', fontWeight: '800', color: 'var(--text-primary)' }}>
            {todayTasks.length}
          </div>
        </div>

        <div
          onClick={() => setActiveTab('upcoming')}
          style={{
            padding: '14px 16px',
            backgroundColor: activeTab === 'upcoming' ? 'rgba(35, 190, 179, 0.08)' : 'var(--surface-card)',
            boxShadow: 'var(--shadow-raised-sm)',
            borderRadius: 'var(--radius-lg)',
            border: activeTab === 'upcoming' ? '1px solid var(--accent-teal)' : '1px solid var(--border-subtle)',
            cursor: 'pointer',
            transition: 'all 0.15s'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)' }}>UPCOMING</span>
            <Calendar size={16} color="var(--color-info)" />
          </div>
          <div style={{ fontSize: '22px', fontWeight: '800', color: 'var(--text-primary)' }}>
            {upcomingTasks.length}
          </div>
        </div>

        <div
          onClick={() => setActiveTab('overdue')}
          style={{
            padding: '14px 16px',
            backgroundColor: activeTab === 'overdue' ? 'rgba(239, 68, 68, 0.08)' : 'var(--surface-card)',
            boxShadow: 'var(--shadow-raised-sm)',
            borderRadius: 'var(--radius-lg)',
            border: activeTab === 'overdue' ? '1px solid var(--color-danger)' : '1px solid var(--border-subtle)',
            cursor: 'pointer',
            transition: 'all 0.15s'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)' }}>OVERDUE</span>
            <AlertCircle size={16} color="var(--color-danger)" />
          </div>
          <div style={{ fontSize: '22px', fontWeight: '800', color: overdueTasks.length > 0 ? 'var(--color-danger)' : 'var(--text-primary)' }}>
            {overdueTasks.length}
          </div>
        </div>

        <div
          onClick={() => setActiveTab('blocked')}
          style={{
            padding: '14px 16px',
            backgroundColor: activeTab === 'blocked' ? 'rgba(245, 158, 11, 0.08)' : 'var(--surface-card)',
            boxShadow: 'var(--shadow-raised-sm)',
            borderRadius: 'var(--radius-lg)',
            border: activeTab === 'blocked' ? '1px solid var(--color-warning)' : '1px solid var(--border-subtle)',
            cursor: 'pointer',
            transition: 'all 0.15s'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)' }}>BLOCKED</span>
            <AlertTriangle size={16} color="var(--color-warning)" />
          </div>
          <div style={{ fontSize: '22px', fontWeight: '800', color: blockedTasks.length > 0 ? 'var(--color-warning)' : 'var(--text-primary)' }}>
            {blockedTasks.length}
          </div>
        </div>

        <div
          onClick={() => setActiveTab('completed')}
          style={{
            padding: '14px 16px',
            backgroundColor: activeTab === 'completed' ? 'rgba(16, 185, 129, 0.08)' : 'var(--surface-card)',
            boxShadow: 'var(--shadow-raised-sm)',
            borderRadius: 'var(--radius-lg)',
            border: activeTab === 'completed' ? '1px solid var(--color-success)' : '1px solid var(--border-subtle)',
            cursor: 'pointer',
            transition: 'all 0.15s'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)' }}>COMPLETED</span>
            <CheckCircle2 size={16} color="var(--color-success)" />
          </div>
          <div style={{ fontSize: '22px', fontWeight: '800', color: 'var(--color-success)' }}>
            {completedTasks.length}
          </div>
        </div>
      </div>

      {/* Tabs Switcher */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border-divider)', gap: '8px' }}>
        {[
          { id: 'today', label: 'Today / In Progress', count: todayTasks.length },
          { id: 'upcoming', label: 'Upcoming', count: upcomingTasks.length },
          { id: 'overdue', label: 'Overdue', count: overdueTasks.length },
          { id: 'blocked', label: 'Blocked Work', count: blockedTasks.length },
          { id: 'completed', label: 'Completed', count: completedTasks.length }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 16px',
              border: 'none',
              background: 'none',
              borderBottom: activeTab === tab.id ? '2px solid var(--accent-teal)' : '2px solid transparent',
              color: activeTab === tab.id ? 'var(--accent-teal)' : 'var(--text-secondary)',
              fontSize: '13px',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <span>{tab.label}</span>
            <Badge size="xs" variant={activeTab === tab.id ? 'teal' : 'neutral'}>
              {tab.count}
            </Badge>
          </button>
        ))}
      </div>

      {/* Actionable Tasks List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {activeTaskList.map((t) => {
          const prj = getProjectInfo(t.projectId);
          const isTaskBlocked = (t.blockedBy || []).length > 0 && (t.blockedBy || []).some((id) => {
            const prereq = allTasks.find((item) => item.id === id);
            return prereq && prereq.status !== 'done';
          });

          return (
            <div
              key={t.id}
              onClick={() => setSelectedTask(t)}
              style={{
                padding: '14px 18px',
                backgroundColor: 'var(--surface-card)',
                boxShadow: 'var(--shadow-raised-sm)',
                borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--border-subtle)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px',
                cursor: 'pointer',
                transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
              className="interactive-raised"
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: 1 }}>
                <input
                  type="checkbox"
                  checked={t.status === 'done'}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    updateTask(t.id, { status: e.target.checked ? 'done' : 'todo' });
                  }}
                  style={{ accentColor: 'var(--accent-teal)', cursor: 'pointer', transform: 'scale(1.15)' }}
                />

                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '11px', fontWeight: '800', color: 'var(--text-muted)' }}>
                      {t.id}
                    </span>
                    <Badge variant="recessed" size="xs">
                      {prj.name}
                    </Badge>
                    {isTaskBlocked && (
                      <Badge variant="danger" size="xs">
                        Blocked
                      </Badge>
                    )}
                  </div>
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: '700',
                      color: t.status === 'done' ? 'var(--text-muted)' : 'var(--text-primary)',
                      textDecoration: t.status === 'done' ? 'line-through' : 'none'
                    }}
                  >
                    {t.title}
                  </span>
                </div>
              </div>

              {/* Right Side Quick Controls & Metadata */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {t.dueDate && (
                  <span style={{ fontSize: '12px', color: t.dueDate < todayStr && t.status !== 'done' ? 'var(--color-danger)' : 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Calendar size={13} />
                    {t.dueDate}
                  </span>
                )}

                <Badge
                  variant={
                    t.priority === 'urgent'
                      ? 'danger'
                      : t.priority === 'high'
                      ? 'warning'
                      : 'neutral'
                  }
                  size="xs"
                >
                  {t.priority}
                </Badge>

                {t.status !== 'done' && t.status !== 'in_progress' && (
                  <Button
                    variant="secondary"
                    size="xs"
                    onClick={(e) => handleStartTask(e, t.id)}
                  >
                    Start
                  </Button>
                )}

                {t.status !== 'done' && (
                  <Button
                    variant="primary"
                    size="xs"
                    icon={<CheckCircle2 size={13} />}
                    onClick={(e) => handleMarkDone(e, t.id)}
                  >
                    Done
                  </Button>
                )}
              </div>
            </div>
          );
        })}

        {activeTaskList.length === 0 && (
          <div
            style={{
              padding: '60px 20px',
              textAlign: 'center',
              backgroundColor: 'var(--surface-recessed)',
              borderRadius: 'var(--radius-card)',
              color: 'var(--text-muted)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <CalendarCheck2 size={32} color="var(--accent-teal)" style={{ opacity: 0.8 }} />
            <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)' }}>
              All caught up in this queue!
            </div>
            <div style={{ fontSize: '13px' }}>No tasks pending for {activeTab}.</div>
          </div>
        )}
      </div>
    </div>
  );
};
